import { useState } from 'react';
import { SUITS, SUIT_NAME, TRICKS_TO_SAVE, type DealInput, type DealResult, type HouseRules, type Suit, type TeamId } from '../game/match';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { Name, Sheet, SuitGlyph, Toggle } from '../components/ui';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (input: DealInput) => void;
  dealNo: number;
  dealer: SeatPlayer;
  /** Who chooses Rang this deal, when known from the rules. */
  caller: SeatPlayer | null;
  callerTeam: TeamId | null;
  rules: HouseRules;
  teams: Teams;
}

/** Mount with `key={dealNo}` so each deal starts from a fresh form. */
export function RecordDealSheet({ open, onClose, onSave, dealNo, dealer, caller, callerTeam, rules, teams }: Props) {
  const [result, setResult] = useState<DealResult | null>(null);
  const [court, setCourt] = useState(false);
  const [trump, setTrump] = useState<Suit | null>(null);
  // Only asked for when Rang goes to the highest bidder.
  const [bidTeam, setBidTeam] = useState<TeamId | null>(null);
  const rangTeamId = callerTeam ?? bidTeam;
  const rangTeam = rangTeamId ? teams[rangTeamId] : null;

  const isCourt = result === 'guaa' && court && rules.court.enabled;
  const guaa = rules.guaaPerLoss + (isCourt ? rules.court.bonus : 0);
  const courtText = rules.court.mode === 'first7InRow' ? 'lost the first 7 tricks in a row' : 'lost all 13 tricks';
  const ready = result !== null && rangTeam !== null;

  const choice = (value: DealResult) => {
    const active = result === value;
    const saved = value === 'saved';
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        onClick={() => {
          setResult(value);
          if (saved) setCourt(false);
        }}
        className={`relative flex min-h-28 flex-col items-center justify-center gap-1 overflow-hidden rounded-2xl p-3 text-center shadow-sm ring-2 transition ${
          active
            ? saved
              ? 'text-white ring-success-500'
              : 'text-white ring-suit-red'
            : 'bg-white text-ink ring-ink/15 hover:ring-ink/40'
        }`}
        style={
          active
            ? { background: saved ? 'linear-gradient(155deg, #22c55e 0%, #15803d 100%)' : 'linear-gradient(155deg, #fb7185 0%, #be123c 100%)' }
            : undefined
        }
      >
        <span className="text-3xl leading-none drop-shadow-sm" aria-hidden>
          {saved ? '✓' : '✗'}
        </span>
        <span className="text-lg font-bold">{saved ? 'Saved' : 'Guaa'}</span>
        <span className={`text-sm ${active ? 'text-white/90' : 'text-ink-soft'}`}>
          {saved ? '0 Guaa' : `+${rules.guaaPerLoss} Guaa`}
        </span>
      </button>
    );
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={`Deal ${dealNo}`}
      footer={
        <div className="space-y-2">
          <p className={`text-center text-sm font-semibold ${result === 'guaa' ? 'text-suit-red' : 'text-success-700'}`} aria-live="polite">
            {!rangTeam
              ? 'Choose which team chose Rang.'
              : result === null
                ? `Did ${rangTeam.name} save themselves?`
                : result === 'saved'
                  ? `${rangTeam.name} saved · no Guaa`
                  : `${rangTeam.name} get +${guaa} Guaa${isCourt ? ' (Court)' : ''}`}
          </p>
          <button
            type="button"
            className="h-14 w-full rounded-xl bg-ink/15 text-lg font-extrabold text-ink shadow-[0_10px_24px_-10px_rgba(217,164,65,0.7)] transition disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
            style={ready ? { background: 'linear-gradient(135deg, #fde68a 0%, #d9a441 60%, #b9812a 100%)' } : undefined}
            disabled={!ready}
            onClick={() => onSave({ result: result!, court: isCourt || undefined, trumpSuit: trump, trumpTeam: rangTeamId })}
          >
            Save deal {dealNo}
          </button>
        </div>
      }
    >
      <div
        className="-mx-5 -mt-4 mb-4 px-5 py-3 text-center"
        style={{ background: 'linear-gradient(135deg, #fdf2d8 0%, #fbe6b6 50%, #f3d58a 100%)' }}
        aria-hidden
      >
        <span className="text-lg tracking-[0.3em] text-ink/70">
          ♠ <span className="text-suit-red">♥</span> ♣ <span className="text-suit-red">♦</span>
        </span>
      </div>

      <p className="text-ink-soft">
        <Name className="font-semibold text-ink">{dealer.name}</Name> dealt
        {caller && (
          <>
            {' '}
            to <Name className="font-semibold text-ink">{caller.name}</Name>
          </>
        )}
        .
      </p>

      {callerTeam ? (
        <p
          className="mt-2 flex items-center gap-2 rounded-xl px-3 py-2 text-lg font-bold"
          style={{ background: `color-mix(in srgb, ${teams[callerTeam].color} 12%, white)` }}
        >
          <span className="h-3.5 w-3.5 shrink-0 rounded-full" style={{ background: teams[callerTeam].color }} aria-hidden />
          {teams[callerTeam].name} chose Rang
        </p>
      ) : (
        <fieldset className="mt-3">
          <legend className="label">Which team chose Rang?</legend>
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {(['A', 'B'] as const).map((id) => (
              <button
                key={id}
                type="button"
                role="radio"
                aria-checked={bidTeam === id}
                onClick={() => setBidTeam(id)}
                className={`flex min-h-12 items-center justify-center gap-2 rounded-xl font-semibold ring-2 transition ${
                  bidTeam === id ? 'text-white ring-transparent' : 'bg-white text-ink ring-ink/20 hover:ring-ink/40'
                }`}
                style={bidTeam === id ? { background: teams[id].color } : undefined}
              >
                <span
                  className="h-3 w-3 rounded-full ring-1"
                  style={{ background: bidTeam === id ? '#fff' : teams[id].color, borderColor: 'transparent' }}
                  aria-hidden
                />
                {teams[id].name}
              </button>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset className="mt-5">
        <legend className="mb-1 font-semibold">
          Did {rangTeam?.name ?? 'they'} make {TRICKS_TO_SAVE} tricks?
        </legend>
        <div className="grid grid-cols-2 gap-3" role="radiogroup">
          {choice('saved')}
          {choice('guaa')}
        </div>
      </fieldset>

      {result === 'guaa' && rules.court.enabled && (
        <div className="mt-4 rounded-xl px-3 py-2 ring-1 ring-amber-400/30" style={{ background: 'linear-gradient(135deg, #fef9ec 0%, #fdf0d0 100%)' }}>
          <Toggle
            label={`Court: they ${courtText}`}
            hint={`Adds ${rules.court.bonus} more Guaa (+${rules.guaaPerLoss + rules.court.bonus} in total).`}
            checked={court}
            onChange={setCourt}
          />
        </div>
      )}

      <fieldset className="mt-5">
        <legend className="label">Rang suit (optional)</legend>
        <div className="grid grid-cols-4 gap-2" role="radiogroup">
          {SUITS.map((s) => (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={trump === s}
              aria-label={SUIT_NAME[s]}
              onClick={() => setTrump(trump === s ? null : s)}
              className={`grid h-12 place-items-center rounded-xl text-2xl ring-2 transition ${
                trump === s ? 'bg-amber-100 ring-amber-400' : 'bg-white ring-ink/20 hover:ring-ink/50'
              }`}
            >
              <SuitGlyph suit={s} />
            </button>
          ))}
        </div>
      </fieldset>
    </Sheet>
  );
}
